import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useForm } from '@/hooks/useForm';
import { InterestSelector } from './InterestSelector';
import { FormInput } from './FormInput';
import { FormTextArea } from './FormTextArea';
import { FormData } from '@/types/form';

const initialFormData: FormData = {
  name: '',
  email: '',
  age: '',
  occupation: '',
  interests: [],
  experience: '',
  goals: '',
  referral: ''
};

export const BetaSignupForm: React.FC = () => {
  const navigate = useNavigate();

  const { values, errors, isSubmitting, handleChange, handleSubmit } = useForm<FormData>({
    initialValues: initialFormData,
    onSubmit: async (formData) => {
      try {
        const response = await fetch("/", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            "form-name": "beta-signup",
            ...formData,
            interests: formData.interests.join(", ")
          }).toString()
        });

        if (!response.ok) throw new Error('Submission failed');
        navigate('/thank-you');
      } catch (error) {
        console.error('Form submission error:', error);
        throw new Error('Form submission failed');
      }
    }
  });

  return (
    <form 
      name="beta-signup"
      method="POST"
      data-netlify="true"
      data-netlify-honeypot="bot-field"
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      <input type="hidden" name="form-name" value="beta-signup" />
      
      <p className="hidden">
        <label>
          Don't fill this out if you're human: <input name="bot-field" />
        </label>
      </p>

      <div className="grid grid-cols-2 gap-6">
        <FormInput
          label="Full Name"
          name="name"
          value={values.name}
          onChange={handleChange}
          error={errors.name}
          required
        />

        <FormInput
          label="Email"
          name="email"
          type="email"
          value={values.email}
          onChange={handleChange}
          error={errors.email}
          required
        />

        <FormInput
          label="Age"
          name="age"
          type="number"
          value={values.age}
          onChange={handleChange}
          error={errors.age}
          required
        />

        <FormInput
          label="Occupation"
          name="occupation"
          value={values.occupation}
          onChange={handleChange}
          error={errors.occupation}
          required
        />
      </div>

      <InterestSelector
        selectedInterests={values.interests}
        onChange={(interests) => handleChange({
          target: { name: 'interests', value: interests }
        } as any)}
      />

      <FormTextArea
        label="Experience with Similar Tools"
        name="experience"
        value={values.experience}
        onChange={handleChange}
        error={errors.experience}
        placeholder="Tell us about your experience with personal development and life optimization tools..."
        required
      />

      <FormTextArea
        label="What are your goals with LIFETRACK?"
        name="goals"
        value={values.goals}
        onChange={handleChange}
        error={errors.goals}
        placeholder="What do you hope to achieve with LIFETRACK?"
        required
      />

      <FormInput
        label="How did you hear about us?"
        name="referral"
        value={values.referral}
        onChange={handleChange}
        error={errors.referral}
        required
      />

      <motion.button
        type="submit"
        disabled={isSubmitting}
        className={`w-full flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${
          isSubmitting
            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
            : 'bg-neon-green/20 text-neon-green hover:bg-neon-green/30'
        }`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <span>{isSubmitting ? 'Submitting...' : 'Submit Application'}</span>
      </motion.button>
    </form>
  );
};